import { EditSaveCancelButtons } from 'features/assetDatabase/components/assetOverview/EditSaveCancelButtons'
import {
  AssetViz,
  AssetVizCompartmentsDisplayState,
  AssetVizHeatmapDisplayState,
  AssetVizHoverEvent,
  AssetVizMode,
  ClipConfig,
} from 'features/assetViz'
import { useRolodexEntries } from 'features/rolodex'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoAssetModel,
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoInspection,
  GeckoMarkupIconID,
  GeckoMarkupLayer,
  RolodexEntryJson,
  RolodexEntryType,
} from 'types'
import { v4 as uuidv4 } from 'uuid'

import { CDFSelector } from './CDFSelector'
import { useImagePreviewerContext } from './ImagePreviewerContext'
import { useImagesContext } from './ImagesContext'
import { ISEditSaveCancelButtonsContainer } from './StyledComponents'

export const CloseModalContainer = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  cursor: pointer;
`

interface ButtonProps {
  $hasBeenEdited: boolean
}

export const SaveButton = styled.button<ButtonProps>`
  padding: 0.25rem 1rem;
  background-color: ${({ theme, $hasBeenEdited }) =>
    $hasBeenEdited ? '#EEEEEE' : theme.forecast.background1};
  color: ${({ theme, $hasBeenEdited }) =>
    $hasBeenEdited ? '#010101' : theme.forecast.textDisabled};
  border: none;
  border-radius: 4px;
  cursor: ${({ $hasBeenEdited }) =>
    $hasBeenEdited ? 'pointer' : 'not-allowed'};
  font-size: 1rem;
  font-weight: 500;
  opacity: ${({ $hasBeenEdited }) => ($hasBeenEdited ? 1 : 0.5)};
`

export const CancelButton = styled.button<ButtonProps>`
  padding: 0.25rem 1rem;
  background-color: transparent;
  color: ${({ theme }) => theme.forecast.text1};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
`

export const AssetVizContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 1rem;
`

const DEFAULT_SCENE_CONFIG = {
  show3D: false,
  showRulers: true,
  heatmapDisplayState: AssetVizHeatmapDisplayState.Sharp,
  showFeatures: true,
  showCompass: true,
  showFeatureLabels: true,
  compartmentsDisplayState: AssetVizCompartmentsDisplayState.NominalPlates,
  showStructures: true,
  showHtmlOverlayDebugColors: false,
  showDebugStats: false,
  showFeatureDebugColors: false,
  showShellCourseDebugColors: false,
  showPlateFeatureDebugColors: false,
  showBinValues: false,
  showLegend: true,
  overrideCameraAspectRatio: -1,
  force11x7AspectRatio: false,
  stretchAssetToFitScreen: false,
  invertColors: true,
  heatmapOpacity: 1,
  clipConfig: new ClipConfig(),
  activePhotogrammetryModel: undefined,
  activePhotogrammetryTexture: undefined,
  setPhotogrammetryTextureLoading: () => {},
  showPhotogrammetryTexture: false,
  hasPhotogrammetryTextureUpdates: false,
}

const ButtonRowId = uuidv4()

interface AssetVizPreviewerProps {
  assetModel: GeckoAssetModel | undefined
  selectedInspection: GeckoInspection
  setSelectedCoords: (coords: GeckoComponentCoords | undefined) => void
  setIsEditingCoordinates: (isEditing: boolean) => void
}

export const AssetVizPreviewer = ({
  assetModel,
  selectedInspection,
  setSelectedCoords,
  setIsEditingCoordinates,
}: AssetVizPreviewerProps) => {
  const [selectedCDF, setSelectedCDF] =
    useState<GeckoComponentDataFrame | null>(null)
  const [coords, setCoords] = useState<GeckoComponentCoords | undefined>(
    undefined
  )
  const { components } = useImagesContext()
  const { selectedCoords, handleSave, handleClose } = useImagePreviewerContext()

  const { componentDataFrames, componentDefaults } = useRolodexEntries({
    type: [
      RolodexEntryType.COMPONENT_DEFAULTS,
      RolodexEntryType.COMPONENT_DATA_FRAME,
      RolodexEntryType.COMPONENT_DATASET,
    ],
    tags: selectedInspection.id
      ? { inspection: [selectedInspection.id] }
      : undefined,
  })

  const markupLayerJson = useMemo(() => {
    const tagsToUse = selectedCDF?.tags ?? {
      [RolodexEntryType.INSPECTION]: selectedInspection.id,
      ...selectedInspection.tags,
    }

    if (!selectedCoords) return undefined
    const xUnit = selectedCoords.x.unit
    const yUnit = selectedCoords.y.unit
    if (!xUnit || !yUnit) return undefined

    const markupLayerID = `inspection-image-indicators-${selectedInspection?.id}`

    return {
      id: markupLayerID,
      type: 'markup-layer',
      data: {
        name: 'Inspection Image Indicators',
        geometry: {
          x_measurement_unit: xUnit.id,
          y_measurement_unit: yUnit.id,
        },
        primitives: !selectedCoords
          ? {}
          : {
              [selectedCoords.toString()]: {
                kind: 'icon',
                icon_id: GeckoMarkupIconID.Camera,
                x: selectedCoords.x.convertTo(xUnit).num,
                y: selectedCoords.y.convertTo(yUnit).num,
              },
            },
      },
      tags: tagsToUse,
    }
  }, [
    selectedCDF,
    selectedCoords,
    selectedInspection.id,
    selectedInspection.tags,
  ])

  const markupLayer = useMemo(
    () =>
      markupLayerJson
        ? GeckoMarkupLayer.CreateFromJSON(
            markupLayerJson as unknown as RolodexEntryJson
          )
        : undefined,
    [markupLayerJson]
  )

  useEffect(() => {
    if (componentDataFrames.length > 0) {
      setSelectedCDF(componentDataFrames[0])
    } else if (componentDataFrames.length === 0) {
      setSelectedCDF(null)
    }
  }, [componentDataFrames])

  const colorThreshold =
    componentDefaults?.[0]?.data.color_scale_thresholds?.get('default')

  const sceneConfig = {
    ...DEFAULT_SCENE_CONFIG,
    overrideCameraAspectRatio: React.useRef<number>(-1),
  }

  const onSelected = () => {
    setSelectedCoords(coords ?? undefined)
    setIsEditingCoordinates(true)
  }

  const onHovered = useCallback(
    (tooltipFrozen: boolean, event?: AssetVizHoverEvent) => {
      setCoords(event?.coords ?? undefined)
    },
    []
  )

  return (
    <>
      {' '}
      <CDFSelector
        componentDataFrames={componentDataFrames}
        selectedInspection={selectedInspection}
        selectedCDF={selectedCDF}
        setSelectedCDF={setSelectedCDF}
      />
      <AssetViz
        id={selectedInspection.id}
        assetModel={assetModel}
        components={components}
        getColorThresholdForComponent={() => colorThreshold}
        componentDataFrames={selectedCDF ? [selectedCDF] : undefined}
        sceneConfig={sceneConfig}
        mode={AssetVizMode.SelectBin}
        onSelected={onSelected}
        onHovered={onHovered}
        markupLayers={markupLayer ? [markupLayer] : undefined}
      />
      <ISEditSaveCancelButtonsContainer>
        <EditSaveCancelButtons
          saveCancelButtons={true}
          handleSave={handleSave}
          handleCancel={handleClose}
          handleEdit={() => {}}
          formID={ButtonRowId}
        />
      </ISEditSaveCancelButtonsContainer>
    </>
  )
}
